import { CHAT_ASSISTANT_PADDING_HORIZONTAL } from '@fruitful/agent-ui';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import React, { useCallback, useEffect, useRef } from 'react';
import { YStack } from 'smoothie';

import FruitfulThinking from '../../../../assets/animations/fruitful-thinking.json';

const SPIN_START_FRAME = 53;
const SPIN_END_FRAME = 169;
const OUTRO_END_FRAME = 226;

const ANIM_DATA = FruitfulThinking as Record<string, unknown>;

type Phase = 'loading' | 'intro' | 'spinning' | 'outro' | 'idle';

interface TypingIndicatorProps {
    isActive: boolean;
}

export const TypingIndicator = ({ isActive }: TypingIndicatorProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const dotLottieRef = useRef<DotLottie | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const phaseRef = useRef<Phase>('loading');
    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;

    const lastFrameRef = useRef(-1);
    const stallCountRef = useRef(0);

    const resetStallDetection = useCallback(() => {
        lastFrameRef.current = -1;
        stallCountRef.current = 0;
    }, []);

    const transitionToOutro = useCallback(
        (inst: DotLottie) => {
            phaseRef.current = 'outro';
            resetStallDetection();
            inst.stop();
            inst.setSegment(SPIN_END_FRAME, OUTRO_END_FRAME);
            inst.setLoop(false);
            inst.play();
        },
        [resetStallDetection],
    );

    const startAnimation = useCallback(
        (canvas: HTMLCanvasElement) => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            if (dotLottieRef.current) {
                dotLottieRef.current.destroy();
                dotLottieRef.current = null;
            }

            phaseRef.current = 'loading';
            resetStallDetection();

            const instance = new DotLottie({
                canvas,
                data: ANIM_DATA,
                autoplay: false,
                loop: false,
            });
            dotLottieRef.current = instance;

            const poll = () => {
                if (!dotLottieRef.current || phaseRef.current === 'idle') {
                    return;
                }
                const inst = dotLottieRef.current;

                if (phaseRef.current === 'loading' && inst.isLoaded) {
                    phaseRef.current = 'intro';
                    resetStallDetection();
                    inst.setSegment(0, SPIN_END_FRAME);
                    inst.setLoop(false);
                    inst.play();
                }

                if (
                    phaseRef.current === 'intro' ||
                    phaseRef.current === 'outro'
                ) {
                    const frame = inst.currentFrame;
                    if (frame === lastFrameRef.current && frame > 0) {
                        stallCountRef.current++;
                    } else {
                        stallCountRef.current = 0;
                    }
                    lastFrameRef.current = frame;

                    if (stallCountRef.current > 5) {
                        resetStallDetection();

                        if (phaseRef.current === 'intro') {
                            if (isActiveRef.current) {
                                phaseRef.current = 'spinning';
                                inst.setSegment(
                                    SPIN_START_FRAME,
                                    SPIN_END_FRAME,
                                );
                                inst.setLoop(true);
                                inst.play();
                            } else {
                                transitionToOutro(inst);
                            }
                        } else if (isActiveRef.current && canvasRef.current) {
                            startAnimation(canvasRef.current);
                            return;
                        } else {
                            phaseRef.current = 'idle';
                            rafIdRef.current = null;
                            return;
                        }
                    }
                }

                rafIdRef.current = requestAnimationFrame(poll);
            };

            rafIdRef.current = requestAnimationFrame(poll);
        },
        [resetStallDetection, transitionToOutro],
    );

    const setCanvasRef = useCallback(
        (node: HTMLCanvasElement | null) => {
            if (!node) {
                if (rafIdRef.current !== null) {
                    cancelAnimationFrame(rafIdRef.current);
                    rafIdRef.current = null;
                }
                if (dotLottieRef.current) {
                    dotLottieRef.current.destroy();
                    dotLottieRef.current = null;
                }
                canvasRef.current = null;
                return;
            }
            canvasRef.current = node;
            startAnimation(node);
        },
        [startAnimation],
    );

    useEffect(() => {
        if (isActive && phaseRef.current === 'idle' && canvasRef.current) {
            startAnimation(canvasRef.current);
        }
        if (
            !isActive &&
            phaseRef.current === 'spinning' &&
            dotLottieRef.current
        ) {
            transitionToOutro(dotLottieRef.current);
        }
    }, [isActive, startAnimation, transitionToOutro]);

    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            dotLottieRef.current?.destroy();
            dotLottieRef.current = null;
        };
    }, []);

    return (
        <YStack paddingHorizontal={CHAT_ASSISTANT_PADDING_HORIZONTAL}>
            <div style={{ width: 40, height: 40 }}>
                <canvas
                    ref={setCanvasRef}
                    style={{ width: '100%', height: '100%' }}
                />
            </div>
        </YStack>
    );
};
